import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, ViewEncapsulation, computed, effect, inject, input, model, signal, untracked, viewChild } from '@angular/core';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { clampedTextBridgeStyles, clampedTextStyleId } from './clamped.text.theme';

/**
 * Line-clamped text with a more/less toggle. Overflow is measured via ResizeObserver (width
 * changes re-clamp); measurement is skipped while expanded so collapsing does not flash the
 * toggle away for a frame.
 *
 * Clamping is float-based, not `-webkit-line-clamp`: a zero-width right float spans the lines
 * above the last one, then the toggle floats right and clears it, so only the last line wraps
 * around the toggle. `-webkit-line-clamp` cannot do that — its truncation point is not a DOM
 * position, so the toggle has to be positioned over the box and space reserved for it on *every*
 * line. The trade is the browser's own ellipsis, which the toggle carries as its own leading "…".
 *
 * Source order differs by state because a right float is placed as high as it fits: collapsed, the
 * toggle must precede the text to be floated at all; expanded, it follows the text so "less" lands
 * after the last word.
 *
 * Expand state is a model: consumers that need persistence across remounts (e.g. AG cell
 * redraws) bind it; others leave it unbound and the model stays local to the instance.
 * Tokens mount via {@link clampedTextBridgeStyles} (same bridge pattern as wb-avatar).
 */
@Component({
  selector: 'w-clamped-text',
  standalone: true,
  template: `
    @if ($text()) {
      <div #lineEl class="clamped-line" [class.expanded]="$expanded()" [class.inset]="$inset()" [style.--clamp-lines]="$maxLines()">
        @if ($showToggle() && !$expanded()) {
          <span class="clamped-lead" aria-hidden="true"></span>
          <button type="button" class="clamped-toggle" (click)="onToggle($event)"><span class="toggle-ellipsis" aria-hidden="true">…</span><span class="toggle-label">more</span></button>
        }

        <p class="clamped-body">{{ $text() }}</p>

        @if ($showToggle() && $expanded()) {
          <button type="button" class="clamped-toggle inline" (click)="onToggle($event)"><span class="toggle-label">less</span></button>
        }
      </div>
    }
  `,
  styleUrls: ['./clamped.text.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClampedTextComponent {

  private readonly document = inject(DOCUMENT);

  /** Body copy to clamp. Empty string renders nothing. */
  public readonly $text = input.required<string>({ alias: 'text' });

  /** Collapsed line count. */
  public readonly $maxLines = input(2, { alias: 'maxLines' });

  /**
   * Two-way expanded state. Leave unbound for instance-local expand; bind when the parent must
   * persist across destroy/recreate (e.g. board cell redraw).
   */
  public readonly $expanded = model(false, { alias: 'expanded' });

  /** Stop toggle click bubbling (e.g. AG cell click also selects the row). */
  public readonly $stopClickPropagation = input(false, { alias: 'stopClickPropagation' });

  /** Field inset. Off by default so list/board cells stay flush. */
  public readonly $inset = input(false, { alias: 'inset' });

  private readonly $lineEl = viewChild<ElementRef<HTMLElement>>('lineEl');

  protected readonly $overflowing = signal(false);
  protected readonly $showToggle = computed(() => this.$expanded() || this.$overflowing());

  public constructor() {

    DesignSystem.Mount(this.document, clampedTextStyleId, clampedTextBridgeStyles);

    // Width drives the line count — re-measure on every resize, not once on init.
    effect(onCleanup => {
      this.$text();
      this.$maxLines();

      // The clipping box, not the paragraph: the paragraph is unconstrained and always fits itself.
      const element = this.$lineEl()?.nativeElement;
      if (!element) return;

      // Measure only while clamped. An open paragraph never overflows, so measuring it would
      // record "fits" — collapsing would then drop the toggle for a frame before the next pass.
      const measure = () => untracked(() => {
        if (this.$expanded()) return;
        this.$overflowing.set(element.scrollHeight > element.clientHeight + 1);
      });

      const observer = new ResizeObserver(measure);
      observer.observe(element);
      measure();
      onCleanup(() => observer.disconnect());
    });
  }

  protected onToggle(event: MouseEvent): void {

    if (this.$stopClickPropagation()) event.stopPropagation();
    this.$expanded.update(v => !v);
  }
}
